/**
 * TerraPanel — main AI assistant chat panel.
 *
 * Fixed right panel with conversation list, message stream, tool call cards,
 * context-aware suggestion chips, feedback controls, animations, and input box.
 */
import { useEffect, useRef, useState, useMemo, useCallback, KeyboardEvent } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PaperAirplaneIcon,
  StopIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  MicrophoneIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  PaperClipIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { isVoiceSupported, startListening } from '@/lib/voiceInput';
import { submitFeedback } from '@/api/terra';
import { useTerraStore } from '@/store/terra';
import { MarkdownMessage } from './MarkdownMessage';
import { ToolCallCard, TOOL_CONFIG } from './ToolCallCard';
import { InlineDataCard } from './InlineDataCard';
import { MentionAutocomplete, useMentionState } from './MentionAutocomplete';
import { StreamingCursor } from './StreamingCursor';
import { readDataVaultContext } from '@/lib/terraBridge';
import type { Message } from '@/api/terra';

// ── Context-aware suggestion chips ─────────────────────────────────────

const SUGGESTION_SETS: Record<string, string[]> = {
  default: [
    'What sites have the highest risk scores?',
    'Show me benzene exceedances across all sites',
    'What is TRRP and how does closure work?',
    'Which wells are predicted to exceed MCLs?',
  ],
  'data-vault': [
    'Which wells exceed regulatory limits?',
    'Show me the concentration trend for this site',
    'What analytes have the highest exceedance ratios?',
    'Navigate to the compliance matrix',
  ],
  polygon: [
    'Show me the plume boundary for this site',
    'What is the predicted plume migration?',
    'Compare historical vs current plume extent',
    'Which contaminants have the largest plumes?',
  ],
  phreatic: [
    'What are the water level trends?',
    'Show groundwater flow direction',
    'Which wells have rising water levels?',
    'Compare seasonal water level patterns',
  ],
  pathfinder: [
    'What remediation technologies are recommended?',
    'Estimate cost to closure for this site',
    'Compare MPE vs bioremediation effectiveness',
    'What is the predicted remediation timeline?',
  ],
  precept: [
    'Which sites should be prioritized for closure?',
    'Show portfolio cost projections',
    'Compare risk scores across all sites',
    'Which sites are improving fastest?',
  ],
  protocol: [
    'Can this site qualify for Tier 1 closure?',
    'What analytes are blocking closure?',
    'Compare concentrations to TRRP Tier 1 PCLs',
    'What is the closure pathway recommendation?',
  ],
};

function getSuggestions(): string[] {
  const ctx = readDataVaultContext();
  if (!ctx) return SUGGESTION_SETS.default;

  const product = ctx.product as string;
  if (product && SUGGESTION_SETS[product]) return SUGGESTION_SETS[product];

  const insights = ctx.chart_insights as Record<string, unknown> | undefined;
  if (insights?.trendDirection) return SUGGESTION_SETS['data-vault'];

  return SUGGESTION_SETS.default;
}

// ── UI action tool names (not data sources) ───────────────────────────
const UI_ACTION_TOOLS = new Set(['navigate_page', 'navigate_chart', 'apply_filters', 'highlight_wells']);

// ── Helper: get tool_call messages preceding an assistant message ──────
function getToolMessagesForAssistant(messages: Message[], assistantIdx: number): Message[] {
  const tools: Message[] = [];
  for (let i = assistantIdx - 1; i >= 0; i--) {
    if (messages[i].role === 'tool_call') {
      tools.unshift(messages[i]);
    } else {
      break;
    }
  }
  return tools;
}

// ── Copy button ───────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-mono-700/50 text-mono-400 hover:text-mono-200 transition-colors"
      title="Copy response"
    >
      {copied ? (
        <CheckIcon className="w-3.5 h-3.5 text-green-400" />
      ) : (
        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

// ── Feedback buttons (thumbs up/down) ─────────────────────────────────

function FeedbackButtons({ messageId, initialRating }: { messageId: string; initialRating: number | null }) {
  const [rating, setRating] = useState<number | null>(initialRating);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Auto-clear the inline error after a few seconds so it doesn't linger.
  useEffect(() => {
    if (!feedbackError) return;
    const t = setTimeout(() => setFeedbackError(null), 4000);
    return () => clearTimeout(t);
  }, [feedbackError]);

  const handleRate = useCallback(async (value: 1 | -1) => {
    const newRating = rating === value ? null : value;
    setRating(newRating);

    if (newRating === -1) {
      setShowFeedbackInput(true);
    } else {
      setShowFeedbackInput(false);
      setFeedbackText('');
    }

    if (newRating) {
      try {
        await submitFeedback(messageId, newRating);
        setFeedbackError(null);
      } catch (err) {
        console.error('TERRA feedback rating failed', err);
        setRating(rating); // roll back optimistic update
        setFeedbackError("Couldn't save feedback");
      }
    }
  }, [messageId, rating]);

  const handleSubmitFeedback = useCallback(async () => {
    if (feedbackText.trim()) {
      try {
        await submitFeedback(messageId, -1, feedbackText.trim());
        setFeedbackError(null);
        setShowFeedbackInput(false);
      } catch (err) {
        console.error('TERRA feedback comment failed', err);
        setFeedbackError("Couldn't send comment");
        return; // keep the input open so the user can retry
      }
    } else {
      setShowFeedbackInput(false);
    }
  }, [messageId, feedbackText]);

  return (
    <>
      <button
        onClick={() => handleRate(1)}
        className={`p-1 rounded transition-colors ${
          rating === 1
            ? 'text-green-400 bg-green-500/20'
            : 'text-mono-400 hover:text-mono-200 hover:bg-mono-700/50'
        }`}
        title="Good response"
      >
        <HandThumbUpIcon className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => handleRate(-1)}
        className={`p-1 rounded transition-colors ${
          rating === -1
            ? 'text-red-400 bg-red-500/20'
            : 'text-mono-400 hover:text-mono-200 hover:bg-mono-700/50'
        }`}
        title="Bad response"
      >
        <HandThumbDownIcon className="w-3.5 h-3.5" />
      </button>
      {showFeedbackInput && (
        <div className="absolute -bottom-12 right-0 flex gap-1 bg-mono-800 border border-mono-700/50 rounded-md p-1 shadow-lg">
          <input
            type="text"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmitFeedback()}
            placeholder="What went wrong?"
            className="text-[10px] bg-transparent border-none text-mono-200 placeholder-mono-500 focus:outline-none w-36 px-1"
            autoFocus
          />
          <button
            onClick={handleSubmitFeedback}
            className="text-[10px] text-cyan-400 hover:text-cyan-300 px-1"
          >
            Send
          </button>
        </div>
      )}
      {feedbackError && (
        <span
          role="alert"
          className="absolute -bottom-5 right-0 text-[10px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-1.5 py-0.5 whitespace-nowrap"
        >
          {feedbackError}
        </span>
      )}
    </>
  );
}

// ── Source citations ──────────────────────────────────────────────────

function SourcesCitation({ toolMessages }: { toolMessages: Message[] }) {
  const [expanded, setExpanded] = useState(false);

  // Filter out UI action tools — they're not data sources
  const dataSources = toolMessages.filter(
    (m) => m.tool_name && !UI_ACTION_TOOLS.has(m.tool_name),
  );
  if (dataSources.length === 0) return null;

  return (
    <div className="mt-2 pt-1.5 border-t border-mono-700/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[10px] text-mono-500 hover:text-mono-300 transition-colors"
      >
        <InformationCircleIcon className="w-3 h-3" />
        <span>{dataSources.length} source{dataSources.length !== 1 ? 's' : ''} used</span>
      </button>
      {expanded && (
        <div className="mt-1 space-y-0.5">
          {dataSources.map((m, i) => {
            const config = m.tool_name ? TOOL_CONFIG[m.tool_name] : null;
            return (
              <div key={i} className="flex items-center gap-1.5 text-[10px] text-mono-400">
                <span>{config?.icon || '⚙️'}</span>
                <span>{config?.label || m.tool_name}</span>
                {m.latency_ms != null && (
                  <span className="text-mono-600">{m.latency_ms}ms</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Error display ─────────────────────────────────────────────────────

function ErrorMessage({ content, onRetry }: { content: string; onRetry?: () => void }) {
  const isRateLimit = content.includes('Rate limit') || content.includes('rate_limited');
  const isTimeout = content.includes('timed out') || content.includes('timeout');

  return (
    <div className="max-w-[95%] bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2 text-sm">
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-red-300 text-xs">{content}</p>
          {isRateLimit && (
            <p className="text-mono-500 text-[10px] mt-1">
              Please wait a moment before sending another message.
            </p>
          )}
          {isTimeout && (
            <p className="text-mono-500 text-[10px] mt-1">
              The request took too long. Try a simpler question or ask about fewer sites.
            </p>
          )}
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="p-1 rounded hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
            title="Retry"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Thinking indicator (three-dot bounce) ─────────────────────────────

function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 text-xs text-mono-400" role="status" aria-label="TERRA is thinking">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
      Thinking...
    </div>
  );
}

// ── Message animation variants ────────────────────────────────────────

const messageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

// ── Main Component ─────────────────────────────────────────────────────

export function TerraPanel() {
  const {
    isOpen,
    close,
    conversations,
    activeConversationId,
    messages,
    isStreaming,
    streamingText,
    activeToolCalls,
    followUpSuggestions,
    citations,
    rateLimitUntil,
    thinkingText,
    fetchConversations,
    newConversation,
    switchConversation,
    removeConversation,
    sendMessage,
    cancelStream,
  } = useTerraStore();

  const [input, setInput] = useState('');
  const [showConvList, setShowConvList] = useState(false);
  const [editingConvId, setEditingConvId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  // Attachment state machine.
  //   'choosing'    — user is picking a site + persistence preference
  //   'processing'  — upload (persistent) or text extract (ephemeral) in flight
  //   'ready'       — either docId (persisted+embedded) or extractedText (ephemeral) is set
  //   'error'       — failed; user can dismiss and retry
  // The persist toggle decides which path runs on confirm: persistent goes through
  // documentsApi.upload (triggers auto-embedding into TERRA's pgvector RAG); ephemeral
  // calls /terra/extract-text and splices the text into the next outgoing message only.
  const [attachedFile, setAttachedFile] = useState<{
    file: File;
    state: 'choosing' | 'processing' | 'ready' | 'error';
    persist: boolean;
    siteId: string | null;
    docId?: string;
    extractedText?: string;
    truncated?: boolean;
    error?: string;
  } | null>(null);
  const [attachSiteOptions, setAttachSiteOptions] = useState<Array<{ id: string; name: string }> | null>(null);
  const [convSearchQuery, setConvSearchQuery] = useState('');
  const [convSearchResults, setConvSearchResults] = useState<any[] | null>(null);
  const [cursorPos, setCursorPos] = useState(0);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Rate limit countdown timer
  useEffect(() => {
    if (!rateLimitUntil) { setRateLimitCountdown(0); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
      setRateLimitCountdown(remaining);
      if (remaining <= 0) useTerraStore.setState({ rateLimitUntil: null });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [rateLimitUntil]);

  // Context-aware suggestions
  const suggestions = useMemo(() => getSuggestions(), [messages.length]);

  // Find last assistant message id for regenerate button
  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i].id;
    }
    return null;
  }, [messages]);

  // Load conversations on first open
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  useEffect(() => {
    if (!userScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingText, activeToolCalls, userScrolledUp]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Scroll handler — detect when user scrolls up
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distanceFromBottom > 100);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setUserScrolledUp(false);
  }, []);

  // @mention autocomplete
  const mentionState = useMentionState(input, cursorPos);

  const handleMentionSelect = useCallback((item: { name: string }) => {
    // Replace @query with the selected name
    const before = input.slice(0, mentionState.atIndex);
    const after = input.slice(mentionState.atIndex + 1 + mentionState.query.length);
    const newInput = `${before}${item.name}${after ? after : ' '}`;
    setInput(newInput);
    setCursorPos(before.length + item.name.length + 1);
    // Focus back on input
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [input, mentionState.atIndex, mentionState.query]);

  const handleConvSearch = useCallback(async (query: string) => {
    setConvSearchQuery(query);
    if (!query.trim()) { setConvSearchResults(null); return; }
    try {
      const { searchConversations } = await import('@/api/terra');
      const results = await searchConversations(query.trim());
      setConvSearchResults(results);
    } catch {
      setConvSearchResults([]);
    }
  }, []);

  const handleAttachFile = async (file: File) => {
    // Default the site to whatever the user is currently viewing, if anything.
    const ctx = readDataVaultContext();
    const defaultSiteId = (ctx?.site_id as string | undefined) ?? null;

    setAttachedFile({
      file,
      state: 'choosing',
      persist: true, // default to saving — that's the value-add for the customer
      siteId: defaultSiteId,
    });

    // Lazy-load the site list the first time the user attaches something
    // so we can offer a selector even outside Data Vault.
    if (!attachSiteOptions) {
      try {
        const { sitesApi } = await import('@/api/sites');
        const resp = await sitesApi.list({ page_size: 200 });
        const items = (resp.items || []).map((s: any) => ({ id: String(s.id), name: s.name }));
        items.sort((a, b) => a.name.localeCompare(b.name));
        setAttachSiteOptions(items);
      } catch (err) {
        console.error('TERRA attach: failed to load sites', err);
        setAttachSiteOptions([]);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleAttachFile(file);
    if (e.target) e.target.value = '';
  };

  const confirmAttachment = useCallback(async () => {
    if (!attachedFile || attachedFile.state !== 'choosing') return;
    const { file, persist, siteId } = attachedFile;

    // Persistent path requires a site so the doc lands in the right vault.
    if (persist && !siteId) {
      setAttachedFile({ ...attachedFile, state: 'error', error: 'Pick a site to save this document to.' });
      return;
    }

    setAttachedFile({ ...attachedFile, state: 'processing' });

    if (persist && siteId) {
      try {
        const { documentsApi } = await import('@/api/documents');
        const result = await documentsApi.upload(siteId, file, 'auto');
        if (result.success && result.document) {
          setAttachedFile({
            ...attachedFile,
            state: 'ready',
            docId: String(result.document.id),
          });
        } else {
          setAttachedFile({ ...attachedFile, state: 'error', error: 'Upload failed' });
        }
      } catch (err: any) {
        console.error('TERRA attach: upload failed', err);
        setAttachedFile({ ...attachedFile, state: 'error', error: err?.message || 'Upload failed' });
      }
    } else {
      // Ephemeral path: extract text server-side, splice into the next message only.
      try {
        const { extractAttachmentText } = await import('@/api/terra');
        const extracted = await extractAttachmentText(file);
        setAttachedFile({
          ...attachedFile,
          state: 'ready',
          extractedText: extracted.text,
          truncated: extracted.truncated,
        });
      } catch (err: any) {
        console.error('TERRA attach: extract failed', err);
        const detail = err?.response?.data?.detail || err?.message || 'Extraction failed';
        setAttachedFile({ ...attachedFile, state: 'error', error: detail });
      }
    }
  }, [attachedFile]);

  const handleSend = async (text?: string) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isStreaming) return;

    setLastUserMessage(trimmed);
    if (!text) setInput('');
    setUserScrolledUp(false);

    if (!activeConversationId) {
      try {
        await newConversation();
      } catch {
        return;
      }
    }

    // Splice the attachment into the outgoing message based on which path the
    // user chose. Persistent => brief mention so TERRA knows to RAG-search it.
    // Ephemeral => inline the extracted text for this turn only.
    if (attachedFile && attachedFile.state === 'ready') {
      const { file, persist, docId, extractedText, truncated } = attachedFile;
      if (persist && docId) {
        const siteName = attachSiteOptions?.find(s => s.id === attachedFile.siteId)?.name;
        const where = siteName ? ` to site "${siteName}"` : '';
        sendMessage(
          `${trimmed}\n\n[Attached "${file.name}"${where}. It has been saved to Site Documents and will be searchable via search_similar_documents shortly.]`,
        );
      } else if (!persist && extractedText) {
        const truncNote = truncated ? ' (truncated)' : '';
        sendMessage(
          `${trimmed}\n\n=== Attached file: ${file.name}${truncNote} (this attachment is not saved) ===\n${extractedText}\n=== End attachment ===`,
        );
      } else {
        sendMessage(trimmed);
      }
    } else {
      sendMessage(trimmed);
    }
    setAttachedFile(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (text: string) => {
    setInput('');
    if (!activeConversationId) {
      newConversation().then(() => sendMessage(text));
    } else {
      sendMessage(text);
    }
    setLastUserMessage(text);
  };

  const toggleVoice = () => {
    if (isRecording) {
      stopRecordingRef.current?.();
      stopRecordingRef.current = null;
      setIsRecording(false);
      return;
    }

    setIsRecording(true);
    const stop = startListening(
      (text, isFinal) => {
        setInput(text);
        if (isFinal) {
          setIsRecording(false);
          stopRecordingRef.current = null;
          setTimeout(() => handleSend(text), 100);
        }
      },
      () => {
        setIsRecording(false);
        stopRecordingRef.current = null;
      },
      () => {
        setIsRecording(false);
        stopRecordingRef.current = null;
      },
    );
    stopRecordingRef.current = stop;
  };

  const handleRetry = () => {
    if (lastUserMessage && !isStreaming) {
      sendMessage(lastUserMessage);
    }
  };

  const handleExport = useCallback(() => {
    if (messages.length === 0) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    const title = conv?.title || 'TERRA Conversation';
    const lines = [`# ${title}\n`, `Exported: ${new Date().toLocaleString()}\n\n---\n`];

    for (const msg of messages) {
      if (msg.role === 'user' && msg.content) {
        lines.push(`\n**You:** ${msg.content}\n`);
      } else if (msg.role === 'assistant' && msg.content) {
        lines.push(`\n**TERRA:**\n\n${msg.content}\n`);
      } else if (msg.role === 'tool_call' && msg.tool_name) {
        lines.push(`\n> Tool: ${msg.tool_name} (${msg.latency_ms ?? 0}ms)\n`);
      }
    }

    const blob = new Blob([lines.join('')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [messages, conversations, activeConversationId]);

  const handleSpeak = useCallback((text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    window.speechSynthesis.speak(utterance);
  }, []);

  const startRename = (convId: string, currentTitle: string) => {
    setEditingConvId(convId);
    setEditTitle(currentTitle);
  };

  const saveRename = async () => {
    if (!editingConvId || !editTitle.trim()) {
      setEditingConvId(null);
      return;
    }
    try {
      const { apiClient } = await import('@/api/client');
      await apiClient.patch(`/terra/conversations/${editingConvId}`, { title: editTitle.trim() });
      fetchConversations();
    } catch {
      // silent fail
    }
    setEditingConvId(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-screen w-[420px] z-[60] flex flex-col bg-mono-950 border-l border-cyan-500/20 shadow-2xl shadow-black/50 animate-slide-in-right font-sans">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-mono-800/60 flex-shrink-0">
        <div className="flex-1 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="3" />
              <line x1="12" y1="2" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="22" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-mono-100 tracking-wide">TERRA</h2>
            <p className="text-[10px] text-mono-500 leading-none">Environmental Intelligence</p>
          </div>
        </div>

        <button
          onClick={() => setShowConvList(!showConvList)}
          className="p-1.5 rounded hover:bg-mono-800/60 text-mono-400 hover:text-mono-200 transition-colors"
          title="Conversations"
        >
          <ChatBubbleLeftRightIcon className="w-4 h-4" />
        </button>

        {messages.length > 0 && (
          <button
            onClick={handleExport}
            className="p-1.5 rounded hover:bg-mono-800/60 text-mono-400 hover:text-mono-200 transition-colors"
            title="Export conversation"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={() => newConversation()}
          className="p-1.5 rounded hover:bg-mono-800/60 text-mono-400 hover:text-mono-200 transition-colors"
          aria-label="New conversation"
          title="New conversation"
        >
          <PlusIcon className="w-4 h-4" />
        </button>

        <button
          onClick={close}
          className="p-1.5 rounded hover:bg-mono-800/60 text-mono-400 hover:text-mono-200 transition-colors"
          aria-label="Close TERRA panel"
          title="Close"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* ── Conversation List (dropdown) ────────────────────────── */}
      {showConvList && (
        <div className="border-b border-mono-800/60 max-h-56 flex-shrink-0 bg-mono-900/50">
          {/* Search input */}
          <div className="px-3 py-2 border-b border-mono-800/40">
            <input
              type="text"
              value={convSearchQuery}
              onChange={(e) => handleConvSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-mono-800/60 border border-mono-700/40 rounded px-2 py-1 text-xs text-mono-200 placeholder-mono-500 focus:outline-none focus:border-cyan-500/40"
            />
          </div>
          <div className="overflow-y-auto max-h-[168px]">
          {(convSearchResults ?? conversations).length === 0 ? (
            <div className="px-4 py-3 text-xs text-mono-500">
              {convSearchQuery ? 'No matching conversations' : 'No conversations yet'}
            </div>
          ) : (
            (convSearchResults ?? conversations).map((c) => (
              <div
                key={c.id}
                className={`
                  flex items-center gap-2 px-4 py-2 cursor-pointer text-xs
                  hover:bg-mono-800/40 transition-colors
                  ${c.id === activeConversationId ? 'bg-cyan-500/5 border-l-2 border-cyan-500/50' : ''}
                `}
                onClick={() => {
                  if (editingConvId !== c.id) {
                    switchConversation(c.id);
                    setShowConvList(false);
                  }
                }}
              >
                {editingConvId === c.id ? (
                  <input
                    className="flex-1 bg-mono-800 border border-mono-600 rounded px-1.5 py-0.5 text-xs text-mono-100 focus:outline-none focus:border-cyan-500/40"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename();
                      if (e.key === 'Escape') setEditingConvId(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <span className="flex-1 text-mono-200 truncate">{c.title}</span>
                )}
                <span className="text-mono-500 text-[10px]">{c.message_count} msgs</span>
                {editingConvId === c.id ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); saveRename(); }}
                    className="p-0.5 rounded hover:bg-cyan-500/20 text-cyan-400 transition-colors"
                  >
                    <CheckIcon className="w-3 h-3" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); startRename(c.id, c.title); }}
                    className="p-0.5 rounded hover:bg-mono-700/40 text-mono-500 hover:text-mono-300 transition-colors"
                    title="Rename"
                  >
                    <PencilIcon className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeConversation(c.id);
                  }}
                  className="p-0.5 rounded hover:bg-red-500/20 text-mono-500 hover:text-red-400 transition-colors"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            ))
          )}
          </div>
        </div>
      )}

      {/* ── Messages Area ───────────────────────────────────────── */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 relative"
        role="log"
        aria-live="polite"
        aria-label="TERRA conversation"
      >
        {messages.length === 0 && !streamingText && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-cyan-500/5 border border-cyan-500/20 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-cyan-500/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1}>
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="3" />
                <line x1="12" y1="2" x2="12" y2="6" />
                <line x1="12" y1="18" x2="12" y2="22" />
                <line x1="2" y1="12" x2="6" y2="12" />
                <line x1="18" y1="12" x2="22" y2="12" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-mono-300 mb-1">Ask TERRA anything</h3>
            <p className="text-xs text-mono-500 mb-5 max-w-[280px]">
              Environmental data, regulatory limits, site analysis, trends, and predictions.
            </p>

            {/* Prompt template cards */}
            <div className="grid grid-cols-2 gap-2 max-w-[380px] mb-4">
              {suggestions.slice(0, 4).map((s) => (
                <button
                  key={s}
                  onClick={() => handleSuggestion(s)}
                  className="text-left px-3 py-2.5 rounded-lg border border-mono-700/50 bg-mono-800/30 hover:border-cyan-500/40 hover:bg-mono-800/50 transition-colors group"
                >
                  <p className="text-[11px] text-mono-300 group-hover:text-cyan-300 leading-snug line-clamp-2">
                    {s}
                  </p>
                </button>
              ))}
            </div>

            <p className="text-[10px] text-mono-600">Type @ to mention a site, well, or analyte</p>
          </div>
        )}

        {/* Rendered messages */}
        <AnimatePresence initial={false}>
          {messages.map((msg, idx) => (
            <motion.div
              key={msg.id}
              initial={messageVariants.initial}
              animate={messageVariants.animate}
              transition={messageVariants.transition}
            >
              {msg.role === 'user' && (
                <div className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 rounded-lg bg-mono-800/60 border border-mono-700/40 text-sm text-mono-100">
                    {msg.content}
                  </div>
                </div>
              )}

              {msg.role === 'tool_call' && msg.tool_name && (
                <ToolCallCard
                  toolCall={{
                    name: msg.tool_name,
                    input: msg.tool_input || {},
                    isActive: false,
                    summary: (msg as any)._summary || `${msg.latency_ms ?? 0}ms`,
                    durationMs: msg.latency_ms ?? undefined,
                  }}
                />
              )}

              {msg.role === 'assistant' && msg.content && (
                msg.content.startsWith('**Error:**') ? (
                  <ErrorMessage
                    content={msg.content.replace('**Error:** ', '')}
                    onRetry={handleRetry}
                  />
                ) : (
                  <div className="group/msg max-w-[95%] bg-mono-800/40 rounded-lg px-4 py-3 text-sm text-mono-100 relative">
                    <MarkdownMessage
                      content={msg.content}
                      citations={citations}
                    />
                    {/* Inline charts/tables/metrics */}
                    {(msg as any)._components?.map((comp: any, ci: number) => (
                      <InlineDataCard key={ci} component={comp} />
                    ))}
                    <SourcesCitation toolMessages={getToolMessagesForAssistant(messages, idx)} />

                    {/* Hover action bar */}
                    <div className="absolute -bottom-3 right-2 flex items-center gap-0.5 bg-mono-800 border border-mono-700/50 rounded-md px-1 py-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity shadow-lg">
                      <CopyButton text={msg.content} />
                      <FeedbackButtons messageId={msg.id} initialRating={msg.rating ?? null} />
                      {window.speechSynthesis && (
                        <button
                          onClick={() => handleSpeak(msg.content || '')}
                          className="p-1 rounded hover:bg-mono-700/50 text-mono-400 hover:text-mono-200 transition-colors"
                          title="Read aloud"
                        >
                          <SpeakerWaveIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {msg.id === lastAssistantId && (
                        <button
                          onClick={handleRetry}
                          disabled={isStreaming}
                          className="p-1 rounded hover:bg-mono-700/50 text-mono-400 hover:text-mono-200 transition-colors disabled:opacity-30"
                          title="Regenerate response"
                        >
                          <ArrowPathIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Follow-up suggestion chips */}
        {!isStreaming && followUpSuggestions.length > 0 && messages.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {followUpSuggestions.map((s) => (
              <button
                key={s}
                onClick={() => handleSuggestion(s)}
                className="text-[11px] px-3 py-1.5 rounded-full border border-mono-700/50 text-mono-400 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Active tool calls */}
        {activeToolCalls.map((tc, i) => (
          <ToolCallCard key={`${tc.name}-${i}`} toolCall={tc} />
        ))}

        {/* Streaming text (rendered as markdown live) */}
        {isStreaming && streamingText && (
          <motion.div
            initial={messageVariants.initial}
            animate={messageVariants.animate}
            transition={messageVariants.transition}
            className="max-w-[95%] bg-mono-800/40 rounded-lg px-4 py-3 text-sm text-mono-100"
          >
            <MarkdownMessage content={streamingText} />
            <StreamingCursor />
          </motion.div>
        )}
        {/* Reasoning trace (collapsible) */}
        {isStreaming && thinkingText && (
          <details className="text-xs text-mono-500 my-1">
            <summary className="cursor-pointer hover:text-mono-300 transition-colors select-none">
              <span className="ml-1">Reasoning...</span>
            </summary>
            <div className="mt-1 px-3 py-2 bg-mono-900/40 rounded text-[11px] text-mono-400 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto">
              {thinkingText}
            </div>
          </details>
        )}
        {isStreaming && !streamingText && activeToolCalls.length === 0 && !thinkingText && (
          <ThinkingIndicator />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Scroll to bottom button ────────────────────────────── */}
      {userScrolledUp && (
        <div className="absolute bottom-[88px] left-1/2 -translate-x-1/2 z-10">
          <button
            onClick={scrollToBottom}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-mono-800 border border-mono-600 text-mono-300 text-xs hover:bg-mono-700 shadow-lg transition-colors"
          >
            <ChevronDownIcon className="w-3.5 h-3.5" />
            New messages
          </button>
        </div>
      )}

      {/* ── Input Area ──────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-mono-800/60">
        {/* Attached file card — adapts to state */}
        {attachedFile && (
          <div className="mb-2 p-2 bg-mono-800/60 border border-mono-700/40 rounded-md text-xs text-mono-300 space-y-2">
            <div className="flex items-center gap-1.5">
              <PaperClipIcon className="w-3 h-3 text-mono-400 flex-shrink-0" />
              <span className="truncate flex-1 text-mono-200">{attachedFile.file.name}</span>
              {attachedFile.state === 'processing' && (
                <span className="text-cyan-400 text-[10px]">
                  {attachedFile.persist ? 'uploading…' : 'extracting…'}
                </span>
              )}
              {attachedFile.state === 'ready' && (
                <span className="text-green-400 text-[10px]">
                  {attachedFile.persist ? 'saved' : 'ready (this turn only)'}
                </span>
              )}
              <button
                onClick={() => setAttachedFile(null)}
                className="text-mono-500 hover:text-mono-300"
                title="Remove attachment"
              >
                <XCircleIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            {attachedFile.state === 'choosing' && (
              <div className="space-y-1.5 pt-1 border-t border-mono-700/40">
                <label className="flex items-center justify-between gap-2 cursor-pointer">
                  <span className="text-mono-400 text-[11px] leading-tight">
                    Save to Site Documents
                    <span className="block text-mono-500 text-[10px]">TERRA will be able to find this in future conversations.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={attachedFile.persist}
                    onChange={(e) => setAttachedFile({ ...attachedFile, persist: e.target.checked })}
                    className="w-3.5 h-3.5 accent-cyan-500"
                  />
                </label>

                <div className="flex items-center gap-2">
                  <label className="text-mono-400 text-[11px] flex-shrink-0">
                    {attachedFile.persist ? 'Site' : 'Site (optional)'}
                  </label>
                  <select
                    value={attachedFile.siteId ?? ''}
                    onChange={(e) =>
                      setAttachedFile({ ...attachedFile, siteId: e.target.value || null })
                    }
                    className="flex-1 bg-mono-900/80 border border-mono-700/60 rounded px-1.5 py-0.5 text-[11px] text-mono-200 focus:border-cyan-500/40 focus:outline-none"
                  >
                    <option value="">— select a site —</option>
                    {(attachSiteOptions ?? []).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-1.5 pt-0.5">
                  <button
                    onClick={() => setAttachedFile(null)}
                    className="px-2 py-0.5 text-[11px] text-mono-400 hover:text-mono-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAttachment}
                    disabled={attachedFile.persist && !attachedFile.siteId}
                    className="px-2 py-0.5 text-[11px] rounded bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Attach
                  </button>
                </div>
              </div>
            )}

            {attachedFile.state === 'ready' && attachedFile.truncated && (
              <p className="text-amber-400 text-[10px]">
                File was long — only the first portion is included in this message.
              </p>
            )}

            {attachedFile.state === 'error' && (
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-red-500/30">
                <span className="text-red-300 text-[10px]">{attachedFile.error || 'Attach failed'}</span>
                <button
                  onClick={() => setAttachedFile({ ...attachedFile, state: 'choosing', error: undefined })}
                  className="text-[10px] text-cyan-300 hover:text-cyan-200"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.docx,.txt"
          onChange={handleFileInputChange}
        />
        <div className="flex items-end gap-2 relative">
          <MentionAutocomplete
            query={mentionState.query}
            visible={mentionState.active}
            onSelect={handleMentionSelect}
            onClose={() => setCursorPos(0)}
          />
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); setCursorPos(e.target.selectionStart ?? 0); }}
            onSelect={(e) => setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={handleKeyDown}
            placeholder="Ask TERRA..."
            rows={1}
            aria-label="Message TERRA"
            className="flex-1 resize-none bg-mono-900/60 border border-mono-700/50 rounded-lg px-3 py-2 text-sm text-mono-100 placeholder-mono-500 focus:border-cyan-500/40 focus:outline-none focus:ring-1 focus:ring-cyan-500/20 max-h-24"
            disabled={isStreaming}
          />

          {isStreaming ? (
            <button
              onClick={cancelStream}
              className="p-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors flex-shrink-0"
              title="Stop generating"
            >
              <StopIcon className="w-4 h-4" />
            </button>
          ) : (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-lg bg-mono-800/40 border border-mono-700/50 text-mono-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-colors flex-shrink-0 disabled:opacity-40"
                title="Attach file"
                disabled={attachedFile?.state === 'processing'}
              >
                <PaperClipIcon className="w-4 h-4" />
              </button>
              {isVoiceSupported() && (
                <button
                  onClick={toggleVoice}
                  className={`p-2 rounded-lg border transition-colors flex-shrink-0 ${
                    isRecording
                      ? 'bg-red-500/20 border-red-500/30 text-red-400 animate-pulse'
                      : 'bg-mono-800/40 border-mono-700/50 text-mono-400 hover:text-cyan-400 hover:border-cyan-500/30'
                  }`}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <MicrophoneIcon className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || rateLimitCountdown > 0}
                className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                aria-label="Send message"
                title="Send message"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        <div className="mt-1.5 text-[10px] text-mono-600 text-center">
          {rateLimitCountdown > 0 ? (
            <span className="text-amber-400">Try again in {rateLimitCountdown}s</span>
          ) : (
            'Shift+Enter for new line'
          )}
        </div>
      </div>
    </div>
  );
}
